import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import _ from 'lodash';
import { lowerCase, startCase } from 'lodash/fp';
import { useState } from 'react';
import {
  Field,
  FieldArray,
  Form,
  InjectedFormProps,
  reduxForm,
  WrappedFieldArrayProps,
} from 'redux-form';
import { FONT_SIZES } from 'shared/styles/variables';
import styled from 'styled-components';

import CheckboxField from 'dashboard/components/FormControls/CheckboxField';
import InputField from 'dashboard/components/FormControls/InputField';
import RadioGroup from 'dashboard/components/FormControls/RadioGroup';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import { GovernmentIdAttributeNameExtraction } from 'dashboard/forms/FormFlagIssues/verificationConstants';
import {
  ReportGenericFraudType,
  ReportGovIDFraudType,
  ReportIssueResponseType,
  ReportIssueType,
  ReportSelfieFraudType,
} from 'dashboard/lib/constants/reportIssue';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { Document, DocumentGeneric } from 'dashboard/models/document';
import { Verification } from 'dashboard/models/verification';
import {
  DOCUMENT_TYPE_TO_DISPLAY_NAME,
  DocumentType,
} from 'dashboard/models/verification-check-config/document-type-detection';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';
import { isRequired, withMessage } from 'lib/utils/formValidation';

interface FlagIssueVerification {
  verificationId?: string;
  attributeName?: string;
  correctValue?: unknown;
}

const FieldContainer = styled('div')`
  margin-bottom: ${Spacing[5]};
`;

const AddToRegistryTermsContainer = styled('div')`
  font-size: ${FONT_SIZES.small};
`;

const LabelsRow = styled('div')`
  display: flex;
`;
const LabelColumn = styled(Label)`
  flex: 1;
  padding-right: ${Spacing[2]};
  width: 100%;
`;

const StyledFieldRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${Spacing[2]};
  margin-right: ${Spacing[5]};
`;

const validate = {
  content: [withMessage('Please provide a description of the issue', isRequired)],
  type: [withMessage('Please provide the type of issue', isRequired)],
  responseType: [withMessage('Please provide a desired response time', isRequired)],
  verificationId: [withMessage('Please select a verification', isRequired)],
  verificationIncorrectAttribute: [
    withMessage('Please select the incorrect attribute', isRequired),
  ],
  verificationCorrectValue: [withMessage('Please provide the correct value', isRequired)],
};

interface FormFlagIssuesProps {
  verifications: Verification[];
  documents: Document[];
  handleCancel: () => void;
}

const FormFlagIssues = ({
  handleCancel,
  handleSubmit,
  submitting,
  pristine,
  verifications,
  documents,
}: FormFlagIssuesProps & InjectedFormProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [currentIssueType, setCurrentIssueType] = useState<ReportIssueType>(undefined);

  const issueTypeOptions = [
    verifications.some((v) => v.type === ApiModel.VerificationDocument) && {
      label: 'Classification',
      value: ReportIssueType.Classification,
    },
    verifications.some((v) =>
      [ApiModel.VerificationDocument, ApiModel.VerificationGovernmentId].includes(v.type),
    ) && {
      label: 'Text Extraction Issue',
      value: ReportIssueType.Extraction,
    },
    verifications.some((v) => v.type === ApiModel.VerificationDatabase) && {
      label: 'Database Matching Issue',
      value: ReportIssueType.DatabaseMatching,
    },
    { label: 'Suspected Fraud', value: ReportIssueType.Fraud },
    { label: 'Other', value: ReportIssueType.Other },
  ].filter(Boolean);

  const fraudTypeOptions = [
    verifications.some((v) => v.type === ApiModel.VerificationGovernmentId) && [
      {
        label: 'Electronic Replica',
        value: ReportGovIDFraudType.ElectronicReplica,
      },
      {
        label: 'Physical Fake',
        value: ReportGovIDFraudType.Fake,
      },
    ],
    verifications.some((v) => v.type === ApiModel.VerificationSelfie) && [
      {
        label: 'GovID Portrait Mismatch',
        value: ReportSelfieFraudType.IDSelfieMismatch,
      },
      {
        label: 'Digital Spoof',
        value: ReportSelfieFraudType.DigitalSpoof,
      },
      {
        label: 'Mask',
        value: ReportSelfieFraudType.Mask,
      },
    ],
    // This is included for both govID and selfie fraud types
    {
      label: 'Other',
      value: ReportGenericFraudType.Other,
    },
  ]
    .filter(Boolean)
    .flat();

  return (
    <Form onSubmit={handleSubmit}>
      <FieldContainer>
        <StyledFieldRow>
          <Field
            component={ReactSelectField}
            name='issueType'
            id='issueType'
            data-test='form-flag-issues__issue-type'
            validate={validate.type}
            disabled={submitting}
            label='Select issue type'
            placeholder='Choose issue type'
            options={issueTypeOptions}
            portal={true}
            onChange={((issueType: ReportIssueType) => setCurrentIssueType(issueType)) as any}
          />
        </StyledFieldRow>
        <Spacer height={2} />

        {ReportIssueType.Fraud === currentIssueType && (
          <>
            <StyledFieldRow>
              <Field
                component={ReactSelectField}
                name='fraudType'
                id='fraudType'
                data-test='form-flag-issues__fraud-type'
                disabled={submitting}
                label='Select fraud type'
                placeholder='Choose fraud type'
                options={fraudTypeOptions}
                portal={true}
              />
            </StyledFieldRow>
          </>
        )}

        {ReportIssueType.Classification === currentIssueType && (
          <>
            <LabelsRow>
              <LabelColumn size='sm'>Verification</LabelColumn>
              <LabelColumn size='sm'>Correct classification</LabelColumn>
              <Spacer width={1.5} height={0} />
            </LabelsRow>
            <FieldArray
              name='issueVerifications'
              component={ClassificationIssueVerificationRows}
              verifications={verifications}
            />
            <Spacer height={2} />
          </>
        )}

        {ReportIssueType.Extraction === currentIssueType && (
          <>
            <LabelsRow>
              <LabelColumn size='sm'>Verification</LabelColumn>
              <LabelColumn size='sm'>Incorrect attribute</LabelColumn>
              <LabelColumn size='sm'>Correct value</LabelColumn>
              <Spacer width={1.5} height={0} />
            </LabelsRow>
            <FieldArray
              name='issueVerifications'
              component={ExtractionIssueVerificationRows}
              verifications={verifications}
              documents={documents}
            />
            <Spacer height={2} />
          </>
        )}

        {ReportIssueType.DatabaseMatching === currentIssueType && (
          <>
            <LabelsRow>
              <LabelColumn size='sm'>Verification</LabelColumn>
              <Spacer width={1.5} height={0} />
            </LabelsRow>
            <FieldArray
              name='issueVerifications'
              component={DatabaseVerificationRows}
              verifications={verifications}
            />
            <Spacer height={2} />
          </>
        )}

        {[ReportIssueType.Fraud, ReportIssueType.Other, ReportIssueType.DatabaseMatching].includes(
          currentIssueType,
        ) && (
          <Field
            component={TextAreaField}
            name='content'
            id='content'
            data-test='form-report-issue__issue-description'
            validate={validate.content}
            disabled={submitting}
            label='Please describe the issue'
            placeholder='Description'
          />
        )}
        {currentIssueType != null && (
          <Field
            component={RadioGroup}
            name='responseType'
            id='responseType'
            data-test='form-flag-issues__no-response'
            disabled={submitting}
            isCompact={true}
            validate={validate.responseType}
            radioLabelValues={[
              {
                label: 'Urgent: Please respond as soon as possible',
                value: ReportIssueResponseType.Urgent,
              },
              {
                label: 'Please respond within a week',
                value: ReportIssueResponseType.WithinAWeek,
              },
              {
                label: 'No response needed',
                value: ReportIssueResponseType.NotRequired,
              },
            ]}
          />
        )}
        {currentIssueType === ReportIssueType.Fraud &&
          verifications.some((v) => v.type === ApiModel.VerificationGovernmentId) && (
            <AddToRegistryTermsContainer>
              <Field
                component={CheckboxField}
                name='addAsFraudulent'
                id='addAsFraudulent'
                data-test='form-flag-issues__add-as-fraudulent'
                label="By checking this box, I allow Persona to add this reported fraudulent ID
                to a registry used to detect fraudulent behavior across organizations. If the ID
                matches that of another submission, it will be shown in the organization's dashboard."
              />
            </AddToRegistryTermsContainer>
          )}
      </FieldContainer>
      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-flag-issues__cancel'
          data-test='form-flag-issues__cancel'
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-flag-issues__submit'
          disabled={submitting || pristine || currentIssueType == null}
          isPending={submitting}
        >
          Flag issues
        </Button>
      </HStack>
    </Form>
  );
};

const InlineFields = styled('div')`
  display: flex;
`;

const Inline = styled('div')`
  flex: 1;
  padding-right: ${Spacing[2]};
`;

const RemoveCheck = styled('div')`
  flex-shrink: 1;
  padding-top: ${Spacing[3]};
  cursor: pointer;
`;

interface ClassificationIssueVerificationRowsProps {
  verifications: Verification[];
}

const ClassificationIssueVerificationRows = ({
  fields,
  verifications,
}: WrappedFieldArrayProps<FlagIssueVerification> & ClassificationIssueVerificationRowsProps) => {
  if (!verifications.some((ver) => ApiModel.VerificationDocument === ver.type)) {
    return null;
  }

  if (fields.length === 0) {
    fields.push({});
  }

  const rows = fields.map((name, index) => {
    return (
      <InlineFields key={index}>
        <Inline>
          <Field
            component={ReactSelectField}
            name={`${name}.verificationId`}
            placeholder='Verification'
            validate={validate.verificationId}
            options={verifications
              // Map before filter to keep label with verification number consistent
              .map((ver, verIndex) => {
                if (ApiModel.VerificationDocument === ver.type) {
                  return {
                    value: ver.id,
                    label: `${VERIFICATION_TYPE_NAMES_SHORT[ver.type]} ${lowerCase(
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      ver.attributes.status,
                    )} #${verIndex + 1}`,
                  };
                }
              })
              .filter(Boolean)}
          />
        </Inline>

        <Inline>
          <Field
            component={ReactSelectField}
            name={`${name}.correctValue`}
            placeholder=''
            validate={validate.verificationCorrectValue}
            options={Object.values(DocumentType).map((type) => {
              return {
                label: DOCUMENT_TYPE_TO_DISPLAY_NAME[type],
                value: type,
              };
            })}
          />
        </Inline>

        {index === 0 ? (
          <Spacer width={1.5} />
        ) : (
          <RemoveCheck
            onClick={() => fields.remove(index)}
            id='row__removeIcon-check'
            data-test='row__removeIcon-check'
          >
            <XIcon />
          </RemoveCheck>
        )}
      </InlineFields>
    );
  });

  return (
    <>
      {rows}
      <Button
        width='auto'
        variant='basic'
        id='form-flag-issues__add'
        onClick={() => fields.push({})}
      >
        Add another
      </Button>
      <Spacer height={3} />
    </>
  );
};

interface ExtractionIssueVerificationRowsProps {
  verifications: Verification[];
  documents: Document[];
}

const ExtractionIssueVerificationRows = ({
  fields,
  verifications,
  documents,
}: WrappedFieldArrayProps<FlagIssueVerification> & ExtractionIssueVerificationRowsProps) => {
  if (
    !verifications.some((ver) =>
      [ApiModel.VerificationDocument, ApiModel.VerificationGovernmentId].includes(ver.type),
    )
  ) {
    return null;
  }

  if (fields.length === 0) {
    fields.push({});
  }

  const rows = fields.map((name, index) => {
    let options: { value: string; label: string }[] = [];
    const verificationId = fields.get(index)?.verificationId;
    const verification = verifications.find((ver) => ver.id === verificationId);
    switch (verification?.type) {
      case ApiModel.VerificationDocument:
        options =
          (documents as DocumentGeneric[])
            .find((document) => document.id === verification.relationships.document?.data.id)
            ?.attributes['extraction-responses']?.map((extraction) => ({
              value: extraction['field-name'],
              label: startCase(extraction['field-name']),
            })) || [];
        break;
      case ApiModel.VerificationGovernmentId:
        options = Object.keys(GovernmentIdAttributeNameExtraction).map((attrName) => ({
          value: attrName,
          label: GovernmentIdAttributeNameExtraction[attrName],
        }));
        break;
    }

    return (
      <InlineFields key={index}>
        <Inline>
          <Field
            component={ReactSelectField}
            name={`${name}.verificationId`}
            placeholder='Verification'
            validate={validate.verificationId}
            options={verifications
              // Map before filter to keep label with verification number consistent
              .map((ver, verIndex) => {
                if (
                  [ApiModel.VerificationDocument, ApiModel.VerificationGovernmentId].includes(
                    ver.type,
                  )
                ) {
                  return {
                    value: ver.id,
                    label: `${VERIFICATION_TYPE_NAMES_SHORT[ver.type]} ${lowerCase(
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      ver.attributes.status,
                    )} #${verIndex + 1}`,
                  };
                }
              })
              .filter(Boolean)}
          />
        </Inline>

        <Inline>
          <Field
            component={ReactSelectField}
            name={`${name}.attributeName`}
            placeholder='Incorrect attribute'
            onChange={() => (fields.get(index).correctValue = '')}
            validate={validate.verificationIncorrectAttribute}
            options={options}
          />
        </Inline>

        <Inline>
          <Field
            component={InputField}
            name={`${name}.correctValue`}
            validate={validate.verificationCorrectValue}
          />
        </Inline>

        {index === 0 ? (
          <Spacer width={1.5} />
        ) : (
          <RemoveCheck
            onClick={() => fields.remove(index)}
            id='row__removeIcon-check'
            data-test='row__removeIcon-check'
          >
            <XIcon />
          </RemoveCheck>
        )}
      </InlineFields>
    );
  });

  return (
    <>
      {rows}
      <Button
        width='auto'
        variant='basic'
        id='form-flag-issues__add'
        onClick={() => fields.push({})}
      >
        Add another
      </Button>
      <Spacer height={3} />
    </>
  );
};

interface DatabaseVerificationRowsProps {
  verifications: Verification[];
}

const DatabaseVerificationRows = ({
  fields,
  verifications,
}: WrappedFieldArrayProps<FlagIssueVerification> & DatabaseVerificationRowsProps) => {
  if (!verifications.some((ver) => ver.type === ApiModel.VerificationDatabase)) {
    return null;
  }

  if (fields.length === 0) {
    fields.push({});
  }

  const rows = fields.map((name, index) => {
    return (
      <InlineFields key={index}>
        <Inline>
          <Field
            component={ReactSelectField}
            name={`${name}.verificationId`}
            placeholder='Verification'
            validate={validate.verificationId}
            options={verifications
              // Map before filter to keep label with verification number consistent
              .map((ver, verIndex) => {
                if (ApiModel.VerificationDatabase === ver.type) {
                  return {
                    value: ver.id,
                    label: `${VERIFICATION_TYPE_NAMES_SHORT[ver.type]} ${lowerCase(
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      ver.attributes.status,
                    )} #${verIndex + 1}`,
                  };
                }
              })
              .filter(Boolean)}
          />
        </Inline>

        {index === 0 ? (
          <Spacer width={1.5} />
        ) : (
          <RemoveCheck
            onClick={() => fields.remove(index)}
            id='row__removeIcon-check'
            data-test='row__removeIcon-check'
          >
            <XIcon />
          </RemoveCheck>
        )}
      </InlineFields>
    );
  });

  return (
    <>
      {rows}
      <Button
        width='auto'
        variant='basic'
        id='form-flag-issues__add'
        onClick={() => fields.push({})}
      >
        Add another
      </Button>
      <Spacer height={3} />
    </>
  );
};

export interface FlagIssuesFormData {
  issueType: ReportIssueType;
  fraudType?: ReportGovIDFraudType | ReportSelfieFraudType | null;
  issueVerifications: FlagIssueVerification[];
  responseType: ReportIssueResponseType;
  addAsFraudulent: boolean;
  content: string;
}

const FORM_NAME = 'flagIssues';

export default reduxForm<FlagIssuesFormData, FormFlagIssuesProps>({
  form: FORM_NAME,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormFlagIssues);
